import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, SearchIcon, XIcon } from 'lucide-react';
import { Logo } from '../brand/Logo';
import { Button } from '../ui/Button';
import { SERVICE_LINES } from '../../data/services';
const INSIGHT_LINKS = [{
  to: '/insights',
  label: 'Articles',
  detail: 'Analysis by sector and service line'
}, {
  to: '/resources',
  label: 'Resources',
  detail: 'eBooks, reports and checklists'
}, {
  to: '/assessment',
  label: 'AI readiness assessment',
  detail: 'Score your organisation in four minutes'
}];
export function Header() {
  const [scrolled, setScrolled] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState<'services' | 'insights' | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  React.useEffect(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setDrawerOpen(false);
        setSearchOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  React.useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchOpen(false);
    navigate('/insights');
    setQuery('');
  };
  const triggerClass = (menu: 'services' | 'insights') => `flex items-center gap-1.5 text-[15px] transition-colors ${openMenu === menu ? 'text-gold' : 'text-ink/80 hover:text-ink'}`;
  return <header className={`sticky top-0 z-40 w-full px-3 transition-all duration-200 sm:px-5 ${scrolled ? 'pt-2' : 'pt-4'}`} data-id="element-848" data-mp="2qnmedi22m7dq">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-6 focus:top-6 focus:z-50 focus:rounded-sm focus:bg-gold focus:px-4 focus:py-2 focus:text-charcoal" data-id="element-849" data-mp="2mkpr3e1l5n3g">
        Skip to content
      </a>

      <div className={`mx-auto flex w-full max-w-[1240px] items-center gap-8 rounded-lg border border-line px-5 backdrop-blur-md transition-all duration-200 sm:px-6 ${scrolled ? 'bg-[#0A2445]/92 py-2.5' : 'bg-[#0A2445]/70 py-3.5'}`} data-id="element-850" data-mp="27xei9jwsgh71">
        <Link to="/" aria-label="Cavalla Solution, home" className="shrink-0" data-id="element-851" data-mp="iey35yxzqihg">
          <Logo variant="reversed" data-id="element-852" data-mp="n9lrnxszvcw1" />
        </Link>

        <div ref={navRef} className="hidden items-center gap-6 whitespace-nowrap lg:flex xl:gap-8" data-id="element-853" data-mp="9gvssssdadhi">
          <NavLink to="/about" className={({
          isActive
        }) => `text-[15px] transition-colors ${isActive ? 'text-gold' : 'text-ink/80 hover:text-ink'}`} data-id="element-854" data-mp="eee60vdf3evb">
            About us
          </NavLink>

          <div className="relative" data-id="element-855" data-mp="1640s7ruyfdlz">
            <button type="button" aria-expanded={openMenu === 'services'} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === 'services' ? null : 'services')} className={triggerClass('services')} data-id="element-856" data-mp="s7dieohwksyn">
              Services
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${openMenu === 'services' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-857" data-mp="3hwxlexazyq8p" />
            </button>
            {openMenu === 'services' && <div className="absolute left-0 top-full z-50 mt-5 w-[340px] rounded-md border border-line bg-panel p-2 shadow-2xl" data-id="element-858" data-mp="3oe0ssbajcin2">
                <Link to="/services" className="block rounded-sm px-4 py-2.5 text-[15px] font-medium text-ink hover:bg-mist" data-id="element-859" data-mp="3jc1p10avvjpl">
                  All services
                </Link>
                {SERVICE_LINES.map(line => <Link key={line.id} to={`/services/${line.id}`} className="block px-4 py-2.5 hover:bg-mist" data-id="element-860" data-mp="ay2t1ynwvyc3">
                    <span className="block text-[15px] font-medium text-ink" data-id="element-861" data-mp="5zrkdr9crtvg">{line.name}</span>
                    <span className="mt-0.5 block text-small text-muted" data-id="element-862" data-mp="2vp3baae5epvd">{line.short}</span>
                  </Link>)}
                <Link to="/augmented-intelligence" className="mt-1 block border-t border-line px-4 py-2.5 pt-3.5 text-[15px] text-body hover:bg-mist" data-id="element-863" data-mp="2qopexplwt4d6">
                  Our method
                </Link>
              </div>}
          </div>

          <NavLink to="/cavalla-gov" className={({
          isActive
        }) => `text-[15px] transition-colors ${isActive ? 'text-gold' : 'text-ink/80 hover:text-ink'}`} data-id="element-864" data-mp="2qi589iht484u">
            Cavalla Gov
          </NavLink>

          <NavLink to="/webinars" className={({
          isActive
        }) => `text-[15px] transition-colors ${isActive ? 'text-gold' : 'text-ink/80 hover:text-ink'}`} data-id="element-865" data-mp="34alcgmmg2c7c">
            Webinars
          </NavLink>

          <NavLink to="/academy" className={({
          isActive
        }) => `text-[15px] transition-colors ${isActive ? 'text-gold' : 'text-ink/80 hover:text-ink'}`} data-id="element-866" data-mp="3e77wj96y4z8b">
            Academy
          </NavLink>

          <div className="relative" data-id="element-867" data-mp="1bvmwdc945pzm">
            <button type="button" aria-expanded={openMenu === 'insights'} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === 'insights' ? null : 'insights')} className={triggerClass('insights')} data-id="element-868" data-mp="1bwo8lk1v8iix">
              Insights
              <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${openMenu === 'insights' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-869" data-mp="1gw156wijh3yp" />
            </button>
            {openMenu === 'insights' && <div className="absolute left-0 top-full z-50 mt-5 w-[340px] rounded-md border border-line bg-panel p-2 shadow-2xl" data-id="element-870" data-mp="1zk63hogffwq4">
                {INSIGHT_LINKS.map(link => <Link key={link.to} to={link.to} className="block rounded-sm px-4 py-2.5 hover:bg-mist" data-id="element-871" data-mp="2dhur341vphyv">
                    <span className="block text-[15px] font-medium text-ink" data-id="element-872" data-mp="29giv8gsew0i8">{link.label}</span>
                    <span className="mt-0.5 block text-small text-muted" data-id="element-873" data-mp="2s4temnxm7s8b">{link.detail}</span>
                  </Link>)}
              </div>}
          </div>
        </div>

        <div className="ml-auto hidden items-center gap-5 lg:flex" data-id="element-874" data-mp="2j829lm13531l">
          {searchOpen ? <form onSubmit={onSearch} className="flex items-center gap-2" data-id="element-875" data-mp="2o75en6h2f99k">
              <label htmlFor="site-search" className="sr-only" data-id="element-876" data-mp="29elgylcoptzi">
                Search insights and resources
              </label>
              <input id="site-search" type="search" autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Search insights" className="w-48 border-b border-white/40 bg-transparent px-1 py-1.5 text-[15px] text-white placeholder:text-white/45" data-id="element-877" data-mp="2e8pq9b9fae3r" />
              <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search" data-id="element-878" data-mp="1vi81tivmi4o7">
                <XIcon className="h-5 w-5 text-white/70" aria-hidden="true" data-id="element-879" data-mp="25zagd2g0s0n1" />
              </button>
            </form> : <button type="button" onClick={() => setSearchOpen(true)} aria-label="Open search" data-id="element-880" data-mp="21wxa8cxd20pq">
              <SearchIcon className="h-5 w-5 text-white transition-colors hover:text-gold" aria-hidden="true" data-id="element-881" data-mp="1wzexsck4x3cz" />
            </button>}

          <Button to="/contact" size="sm" shape="pill" className="px-5 py-2.5" data-id="element-882" data-mp="1s0u1go8073tt">
            Book a discovery call
          </Button>
          <Link to="/portal/login" className="text-[15px] text-white underline underline-offset-4 transition-colors hover:text-gold" data-id="element-883" data-mp="1i9g3wlzm4e3b">
            Client login
          </Link>
        </div>

        <button type="button" className="ml-auto lg:hidden" aria-expanded={drawerOpen} aria-controls="mobile-nav" aria-label={drawerOpen ? 'Close menu' : 'Open menu'} onClick={() => setDrawerOpen(open => !open)} data-id="element-884" data-mp="199azmkpptvtd">
          {drawerOpen ? <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-885" data-mp="3kv3azmpix0pb" /> : <MenuIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-886" data-mp="3pwixlj7cnf9t" />}
        </button>
      </div>

      {drawerOpen && <div id="mobile-nav" className="fixed inset-x-0 bottom-0 top-[76px] z-40 overflow-y-auto bg-charcoal lg:hidden" data-id="element-887" data-mp="3tuys085rfpx9">
          <nav aria-label="Mobile" className="flex flex-col gap-1 px-5 py-6" data-id="element-888" data-mp="3253rdfcgcdta">
            <Link to="/about" className="px-1 py-3 text-[17px] text-white" data-id="element-889" data-mp="367qnvtk2imwt">
              About us
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-890" data-mp="3aygs33grcjpx" />
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-891" data-mp="3b0bfcofi4nx6">Services</p>
            {SERVICE_LINES.map(line => <Link key={line.id} to={`/services/${line.id}`} className="px-1 py-3 text-[17px] text-white" data-id="element-892" data-mp="2iguni81jqjdy">
                {line.name}
              </Link>)}
            <Link to="/augmented-intelligence" className="px-1 py-3 text-[17px] text-white" data-id="element-893" data-mp="25vkoldnn9vsl">
              Our method
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-894" data-mp="29ypor9k95le2" />
            <Link to="/cavalla-gov" className="px-1 py-3 text-[17px] text-white" data-id="element-895" data-mp="2ewiq9pam1r80">
              Cavalla Gov
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-896" data-mp="1i71fivwrtezg" />
            <Link to="/webinars" className="px-1 py-3 text-[17px] text-white" data-id="element-897" data-mp="1i45jjd4hh0dn">
              Webinars
            </Link>
            <Link to="/academy" className="px-1 py-3 text-[17px] text-white" data-id="element-898" data-mp="1r76m53u549ck">
              Academy
            </Link>
            <div className="my-3 h-px bg-white/15" data-id="element-899" data-mp="1vxwrih2kgc79" />
            <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-900" data-mp="1vzq0w44kdaiy">Insights</p>
            {INSIGHT_LINKS.map(link => <Link key={link.to} to={link.to} className="px-1 py-3 text-[17px] text-white" data-id="element-901" data-mp="13gamxlncubva">
                {link.label}
              </Link>)}
            <div className="my-3 h-px bg-white/15" data-id="element-902" data-mp="qyqfsg1tvt4d" />
            <Link to="/portal/login" className="px-1 py-3 text-[17px] text-white" data-id="element-903" data-mp="r01gu83w2mrs">
              Client login
            </Link>
            <Button to="/contact" className="mt-5 w-full" size="lg" shape="pill" data-id="element-904" data-mp="m1q6zxdlgc9k">
              Book a discovery call
            </Button>
          </nav>
        </div>}
    </header>;
}